import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../App';
import NotificationBell from '../admin/NotificationBell';
import { LogOut, Home, Menu, X, LayoutGrid, KeyRound } from 'lucide-react';
import { ChangePasswordDialog } from '../ChangePasswordDialog';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_736b5617-324a-4027-a0b5-949393e5cf90/artifacts/te4le6nd_Untitled%20design.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

const NAV_ITEMS = [
  { label: 'Dashboard', to: '/admin' },
  { label: 'Member Portal CMS', to: '/admin/member-portal' },
  { label: 'Leads', to: '/admin/leads' },
  { label: 'Block List', to: '/admin/block-list' },
  { label: 'Resource Downloads', to: '/admin/resource-downloads' },
  { label: 'Free Resources', to: '/admin/resources' },
  { label: 'Site Manager', to: '/admin/site-manager' },
  { label: 'Bookings', to: '/admin/bookings' },
  { label: 'Calendar', to: '/admin/calendar' },
  { label: 'Time Requests', to: '/admin/time-requests', badgeKey: 'time_requests' },
  { label: 'Booking Brands', to: '/admin/booking-brands' },
  { label: 'Brand Revenue', to: '/admin/brand-revenue' },
  { label: 'Coaching Packages', to: '/admin/coaching-packages' },
  { label: 'Memberships', to: '/admin/memberships' },
  { label: 'Clients', to: '/admin/clients' },
  { label: 'Programs', to: '/admin/programs' },
  { label: 'Events', to: '/admin/events' },
  { label: 'Payment Links', to: '/admin/payment-links' },
  { label: 'Quick Send', to: '/admin/quick-send' },
  { label: 'Video Sessions', to: '/admin/video-sessions' },
  { label: 'Affiliates', to: '/admin/affiliates' },
  { label: 'AI Tools', to: '/admin/ai-tools' },
  { label: 'Clarity Hub', to: '/admin/clarity-hub' },
  { label: 'Combined Interview', to: '/admin/combined-interview', coachOnly: true },
  { label: '7 Stage Jumpstart Sessions', to: '/admin/jumpstart-sessions', coachOnly: true },
  { label: 'Nonprofit Accelerator', to: '/admin/nonprofit-accelerator' },
  { label: 'Website Vision Builder', to: '/admin/website-vision' },
  { label: 'Certificates', to: '/admin/certificates' },
  { label: 'Email Templates', to: '/admin/email-templates' },
  { label: 'Comp Sessions', to: '/admin/comp-sessions' },
  { label: 'Workflows', to: '/admin/workflows' },
  { label: 'Ticketed Events', to: '/admin/ticketed-events' },
  { label: 'Webinars', to: '/admin/webinars' },
  { label: 'Group Sessions', to: '/admin/group-sessions' },
  { label: 'Coaches', to: '/admin/coaches' },
  { label: 'Reminders & Blasts', to: '/admin/event-reminders' },
  { label: 'Blasts', to: '/admin/blasts' },
  { label: 'Subscribers', to: '/admin/subscribers' },
  { label: 'Services', to: '/admin/services' },
  { label: 'Scheduling', to: '/admin/scheduling' },
  { label: 'Automation', to: '/admin/automation' },
  { label: 'Analytics', to: '/admin/analytics' },
  { label: 'Earnings', to: '/admin/earnings' },
  { label: 'Contracts', to: '/admin/contracts' },
  { label: 'Links', to: '/admin/links' },
  { label: 'Users', to: '/admin/users' },
  { label: 'Activity', to: '/admin/activity' },
  { label: 'Knowledge Base', to: '/admin/knowledge-base' },
  { label: 'Social Studio', to: '/admin/social-studio' },
  { label: 'Flyer Studio', to: '/admin/flyer-studio' },
  { label: 'QR Analytics', to: '/admin/qr-analytics' },
  { label: 'Intelligence Engine', to: '/admin/intelligence' },
  { label: 'Intelligence Settings', to: '/admin/intelligence-settings' },
  { label: 'Shared Plans', to: '/admin/social-studio/shares' },
  { label: 'Stripe Setup', to: '/admin/stripe-setup' },
  { label: 'Maintenance', to: '/admin/maintenance' },
  { label: 'Deploy Readiness', to: '/admin/deploy', superAdminOnly: true },
  { label: 'Roadmap', to: '/admin/roadmap' },
];

export default function AdminLayout({ title, children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isCoach = user?.is_coach === true || user?.role === 'coach';
  const isSuperAdmin = user?.role === 'super_admin';
  const navItems = NAV_ITEMS.filter((item) => (!item.coachOnly || isCoach) && (!item.superAdminOnly || isSuperAdmin));

  const [badges, setBadges] = useState({});
  useEffect(() => {
    if (!isAdmin) return undefined;
    let active = true;
    const load = async () => {
      try {
        const r = await axios.get(`${API_URL}/api/admin/time-requests/pending-count`, { withCredentials: true });
        if (active) setBadges((b) => ({ ...b, time_requests: r.data.count || 0 }));
      } catch { /* silent */ }
    };
    load();
    const id = setInterval(load, 45000);
    window.addEventListener('time-requests-updated', load);
    return () => { active = false; clearInterval(id); window.removeEventListener('time-requests-updated', load); };
  }, [isAdmin]);

  const [pwOpen, setPwOpen] = useState(false);
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const closeDrawer = () => setDrawerOpen(false);

  const isActive = (to) => location.pathname === to || (to !== '/admin' && location.pathname.startsWith(to + '/'));

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-layout">
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <header className="bg-[#0A0F1C] text-white">
        <div className="container-custom">
          <div className="flex items-center justify-between py-4">
            <Link to={isAdmin ? "/admin" : "/portal"} className="flex items-center gap-3">
              <img src={LOGO_URL} alt="DCA Logo" className="h-10" />
              <div>
                <div className="font-bold text-sm">{isAdmin ? 'Admin Console' : 'My Workspace'}</div>
                <div className="text-xs text-[#94A3B8]">{title || 'Manage your business'}</div>
              </div>
            </Link>
            <div className="flex items-center gap-3 sm:gap-4">
              <span className="text-sm text-[#94A3B8] hidden md:inline">Welcome, {user?.name}</span>
              {isAdmin && <NotificationBell />}
              {isAdmin && (
                <Link to="/choose-workspace" data-testid="admin-switch-workspace"
                  className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold text-white bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-full transition-colors"
                  title="Switch between Admin Console and Member Portal">
                  <LayoutGrid size={14} /> Switch workspace
                </Link>
              )}
              <Link to={isAdmin ? "/" : "/portal"} className="text-[#94A3B8] hover:text-white p-1" title={isAdmin ? "View Site" : "Back to Portal"} aria-label={isAdmin ? "View site" : "Back to portal"}>
                <Home size={20} />
              </Link>
              <button onClick={() => setPwOpen(true)} className="text-[#94A3B8] hover:text-white p-1" title="Change password" aria-label="Change password" data-testid="admin-change-password-btn">
                <KeyRound size={20} />
              </button>
              <button onClick={handleLogout} className="text-[#94A3B8] hover:text-white p-1" title="Logout" aria-label="Logout" data-testid="admin-logout-btn">
                <LogOut size={20} />
              </button>
              {isAdmin && (
                <button
                  onClick={() => setDrawerOpen(true)}
                  className="lg:hidden text-white p-1.5 -mr-1.5"
                  aria-label="Open menu"
                  data-testid="admin-mobile-menu-btn"
                >
                  <Menu size={22} />
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {isAdmin && drawerOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 bg-black/50"
          onClick={closeDrawer}
          data-testid="admin-mobile-drawer-overlay"
        >
          <aside
            className="absolute left-0 top-0 h-full w-72 bg-white shadow-xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Admin menu"
            data-testid="admin-mobile-drawer"
          >
            <div className="flex items-center justify-between px-4 py-4 border-b border-[#E2E8F0]">
              <div className="font-bold text-sm text-[#0A0F1C]">Admin Menu</div>
              <button onClick={closeDrawer} className="p-1 -mr-1" aria-label="Close menu" data-testid="admin-mobile-drawer-close">
                <X size={20} />
              </button>
            </div>
            <nav className="py-2">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={closeDrawer}
                  className={`flex items-center justify-between gap-2 px-5 py-2.5 text-sm border-l-4 ${isActive(item.to) ? 'border-[#1D4ED8] text-[#1D4ED8] font-bold bg-[#EFF6FF]' : 'border-transparent text-[#475569] hover:bg-[#F8FAFC] hover:text-[#1D4ED8]'}`}
                  data-testid={`admin-mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <span>{item.label}</span>
                  {item.badgeKey && badges[item.badgeKey] > 0 && (
                    <span className="bg-[#F97316] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1.5" data-testid={`admin-mobile-nav-badge-${item.badgeKey}`}>
                      {badges[item.badgeKey] > 99 ? '99+' : badges[item.badgeKey]}
                    </span>
                  )}
                </Link>
              ))}
            </nav>
          </aside>
        </div>
      )}

      {/* Desktop: 2-column layout with LEFT sidebar navigation (admins only) */}
      <div className="lg:flex lg:items-start max-w-[1480px] mx-auto px-4 sm:px-6 lg:px-8 py-6 gap-6">
        {isAdmin && (
        <aside
          className="hidden lg:block w-64 flex-shrink-0 sticky top-6 self-start"
          aria-label="Admin navigation"
          data-testid="admin-sidebar"
        >
          <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden shadow-sm">
            <div className="px-4 py-3 border-b border-[#E2E8F0] bg-[#F8FAFC]">
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#94A3B8]">Admin Console</div>
              <div className="text-sm font-bold text-[#0A0F1C] truncate">{title || 'Manage your business'}</div>
            </div>
            <nav className="py-1.5 max-h-[calc(100vh-160px)] overflow-y-auto">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex items-center justify-between gap-2 px-4 py-2 text-sm border-l-[3px] transition-colors ${isActive(item.to) ? 'border-[#1D4ED8] text-[#1D4ED8] font-bold bg-[#EFF6FF]' : 'border-transparent text-[#475569] hover:bg-[#F8FAFC] hover:text-[#1D4ED8]'}`}
                  data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <span className="truncate">{item.label}</span>
                  {item.badgeKey && badges[item.badgeKey] > 0 && (
                    <span className="bg-[#F97316] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1.5 shrink-0" data-testid={`admin-nav-badge-${item.badgeKey}`}>
                      {badges[item.badgeKey] > 99 ? '99+' : badges[item.badgeKey]}
                    </span>
                  )}
                </Link>
              ))}
            </nav>
          </div>
        </aside>
        )}
        <main className="flex-1 min-w-0">{children}</main>
      </div>
    </div>
  );
}
